import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { SessionContextProvider } from '@supabase/auth-helpers-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PipecatClientProvider } from '@pipecat-ai/client-react';
import { PipecatClient } from '@pipecat-ai/client-js';
import { DailyTransport } from '@pipecat-ai/daily-transport';
import domNodeErrorDebugger from './lib/domNodeErrorDebugger';

import { supabase } from './lib/supabase';
import { PHProvider } from './lib/posthog';
import EhPageView from './components/EhPageview';
import { EhLayout } from './components/EhLayout';
import { GemBalanceProvider } from './contexts/GemBalanceContext';
import { CallProvider } from './contexts/CallContext';
import HomePage from './pages/HomePage';
import ChatPage from './pages/ChatPage';
import ContactsPage from './pages/ContactsPage';
import SettingsPage from './pages/SettingsPage';
import SubscriptionPage from './pages/SubscriptionPage';
import GemsPage from './pages/GemsPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import CreateCharacterPage from './pages/CreateCharacterPage';
import StudioPage from './pages/StudioPage';
import GalleryPage from './pages/GalleryPage';

import { useSupabaseAuthEvents } from './hooks/useSupabaseAuthEvents'

function App() {
  const { listenToAuthEvents } = useSupabaseAuthEvents(supabase);
  // Machine translation errors may happen depsite preventive measures (no unwrapped Text nodes)
  // https://github.com/facebook/react/issues/11538#issuecomment-2136914960
  domNodeErrorDebugger({ highlightErrorNode: false, logErrorNodeToConsole: true });

  useEffect(() => {
    const subscription = listenToAuthEvents();
    return subscription.unsubscribe;
  }, [listenToAuthEvents])

  const queryClient = new QueryClient()

  const client = new PipecatClient({
    transport: new DailyTransport(),
    enableMic: true,
    enableCam: false,
    enableScreenShare: false,
  });

  const Providers = ({ children }: { children: React.ReactNode }) => {
    return (
      <PHProvider>
        <SessionContextProvider supabaseClient={supabase}>
          <PipecatClientProvider client={client}>
            <GemBalanceProvider>
              <CallProvider>
                <QueryClientProvider client={queryClient}>
                  {children}
                </QueryClientProvider>
              </CallProvider>
            </GemBalanceProvider>
          </PipecatClientProvider>
        </SessionContextProvider>
      </PHProvider>
    )
  }

  return (
    <Providers>
      <Router>
        <EhLayout>

          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/c" element={<ChatPage />} />
            <Route path="/contacts" element={<ContactsPage />} />
            <Route path="/ai-girlfriend/:charSlug" element={<ChatPage />} />
            <Route path="/ai-boyfriend/:charSlug" element={<ChatPage />} />
            <Route path="/c/:charSlug/:chatIdSlug" element={<ChatPage />} />
            <Route path="/c/:charSlug" element={<ChatPage />} />
            <Route path="/create-character" element={<CreateCharacterPage />} />
            <Route path="/studio" element={<StudioPage />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/gallery/:characterId/:characterSlug" element={<GalleryPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/subscription" element={<SubscriptionPage />} />
            <Route path="/gems" element={<GemsPage />} />
            <Route path="/legal/terms-of-service" element={<TermsOfServicePage />} />
            <Route path="/legal/privacy-policy" element={<PrivacyPolicyPage />} />
          </Routes>
          <EhPageView />
        </EhLayout>
      </Router>
    </Providers>
  );
}

export default App;
